import { useState, useEffect, FormEvent } from 'react';
import { useContext } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { UserContext } from '../../App';
import { login, checkAuthMethod, initiateSSO, CheckAuthMethodResponse } from '../../services/authService';
import axios from 'axios';
import { detectAndroidDevice } from '../../utils/deviceDetection';
import { isCarrier, isAnySpotter } from '../../utils/roleHelper';

const WRITE_API_URL = process.env.REACT_APP_WRITE_API_URL;

type LoginStep = 'email' | 'password' | 'sso_redirecting';

const Login = () => {
  // @ts-ignore
  const { user, setUser } = useContext(UserContext);
  const [searchParams] = useSearchParams();

  // Step management for SSO flow
  const [step, setStep] = useState<LoginStep>('email');

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotPassword, setForgotPassword] = useState(false);

  // Auth method result (stored for potential future use, e.g., showing provider name)
  const [, setAuthMethod] = useState<CheckAuthMethodResponse | null>(null);

  // Check if we're in android mode based on device detection
  const isAndroidDevice = detectAndroidDevice();
  const isSpotterRole = isAnySpotter(user);
  const isAndroidMode = isAndroidDevice || isSpotterRole;

  // Handle SSO error returns from URL params
  useEffect(() => {
    const ssoError = searchParams.get('sso_error');
    const errorMessage = searchParams.get('error_message');

    if (ssoError) {
      setError(errorMessage || 'SSO login failed. Please try again.');
      // Clean up URL
      window.history.replaceState({}, '', '/login');
    }
  }, [searchParams]);

  if (!!user) {
    // Redirect based on user role and device mode
    if (isAndroidMode) {
      return <Navigate to="/taskapp" replace />;
    }
    if (isCarrier(user)) {
      return <Navigate to="/carrier/trailers" replace />;
    }
    // For other users, redirect will be handled after YMS check in handleLogin
    return <Navigate to="/" replace />;
  }

  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await checkAuthMethod(email);
      setAuthMethod(result);

      if (result.auth_type === 'sso' && result.redirect_url) {
        setStep('sso_redirecting');
        // Brief delay to show message before redirect
        setTimeout(() => {
          initiateSSO(result.redirect_url!);
        }, 500);
      } else {
        setStep('password');
      }
    } catch (err: any) {
      // On error, fall back to password flow (don't reveal if user exists)
      console.error('Auth method check failed, falling back to password:', err);
      setStep('password');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const loginResponse = await login({ email, password });
      setUser(loginResponse);
    } catch (error: any) {
      console.error('Login failed', error);
      setError(error.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setPassword('');
    setError('');
    setAuthMethod(null);
  };

  // Render SSO redirecting state
  if (step === 'sso_redirecting') {
    return (
      <div className={`flex grow items-center justify-center h-full ${!isAndroidMode ? '-ml-48' : ''}`}>
        <div className={`flex flex-col gap-4 bg-white shadow items-center ${
          isAndroidMode
            ? 'w-full max-w-sm mx-4 p-6 rounded-lg'
            : 'p-8'
        }`}>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
          <p className="text-center mt-4">Redirecting to SSO login...</p>
          <p className="text-sm text-gray-500 text-center">
            You will be redirected automatically.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex grow items-center justify-center h-full ${!isAndroidMode ? '-ml-48' : ''}`}>
      <div className={`flex flex-col gap-4 bg-white shadow ${
        isAndroidMode
          ? 'w-full max-w-sm mx-4 p-6 rounded-lg'
          : 'p-8'
      }`}>
        {forgotPassword ? (
          <ForgotPasswordForm
            email={email}
            setEmail={setEmail}
            setForgotPassword={setForgotPassword}
            isAndroidMode={isAndroidMode}
          />
        ) : step === 'email' ? (
          // Email step
          <form onSubmit={handleEmailSubmit}>
            <div className="flex flex-col gap-4">
              <h1 className={`text-center ${isAndroidMode ? 'text-2xl font-bold mb-4' : ''}`}>
                Welcome Back
              </h1>
              <input
                className={`border border-gray-400 bg-[#FBFBFB] ${
                  isAndroidMode
                    ? 'p-4 text-lg rounded-lg'
                    : 'p-2'
                }`}
                type="email"
                placeholder="Email"
                onChange={(e) => setEmail(e.target.value)}
                value={email}
                disabled={loading}
                autoFocus
                required
              />
              {!!error && <p className="text-red-500">{error}</p>}
              <button
                className={`bg-sky-600 hover:bg-sky-500 text-white rounded ${
                  isAndroidMode
                    ? 'p-4 text-lg font-semibold'
                    : 'p-2'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                type="submit"
                disabled={loading || !email}
              >
                {loading ? 'Checking...' : 'Continue'}
              </button>
            </div>
          </form>
        ) : (
          // Password step
          <form onSubmit={handlePasswordSubmit}>
            <div className="flex flex-col gap-4">
              <h1 className={`text-center ${isAndroidMode ? 'text-2xl font-bold mb-4' : ''}`}>
                Welcome Back
              </h1>

              {/* Email display with change option */}
              <div className="flex items-center justify-between border border-gray-300 bg-gray-50 rounded p-2">
                <span className="text-gray-700 truncate">{email}</span>
                <button
                  type="button"
                  onClick={handleBackToEmail}
                  className="text-sky-600 hover:text-sky-500 text-sm ml-2 whitespace-nowrap"
                >
                  Change
                </button>
              </div>

              <input
                className={`border border-gray-400 bg-[#FBFBFB] ${
                  isAndroidMode
                    ? 'p-4 text-lg rounded-lg'
                    : 'p-2'
                }`}
                type="password"
                placeholder="Password"
                onChange={(e) => setPassword(e.target.value)}
                value={password}
                disabled={loading}
                autoFocus
                required
              />
              {!!error && <p className="text-red-500">{error}</p>}
              <button
                onClick={() => setForgotPassword(true)}
                type="button"
                className={`hover:underline text-left ${
                  isAndroidMode
                    ? 'text-base text-sky-600 py-2'
                    : 'text-sm'
                }`}
              >
                Forgot Password?
              </button>
              <button
                className={`bg-sky-600 hover:bg-sky-500 text-white rounded ${
                  isAndroidMode
                    ? 'p-4 text-lg font-semibold'
                    : 'p-2'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                type="submit"
                disabled={loading || !password}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const ForgotPasswordForm = ({
  email,
  setEmail,
  setForgotPassword,
  isAndroidMode
}: {
  email: string;
  setEmail: (e: string) => void;
  setForgotPassword: (e: boolean) => void;
  isAndroidMode: boolean;
}) => {
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState('');

  const handleForgotPasswordClick = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${WRITE_API_URL?.replace('web-btb', 'api-btb')}/api/v1/admin/send_reset_email`,
        { email: email }
      );

      setEmailSent(true);
      setLoading(false);
      setError('');
    } catch (error) {
      console.error('Error sending reset email', error);
      setError('Error sending reset email');
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <h1 className={`text-center ${isAndroidMode ? 'text-2xl font-bold mb-4' : ''}`}>
        Forgot Your Password?
      </h1>
      {emailSent ? (
        <p className="text-green-500">Password reset email sent</p>
      ) : (
        <>
          <label className={isAndroidMode ? 'text-base' : 'text-sm'}>Email:</label>
          <input
            className={`border border-gray-400 bg-[#FBFBFB] ${
              isAndroidMode
                ? 'p-4 text-lg rounded-lg'
                : 'p-2'
            }`}
            type="text"
            placeholder="Email"
            onChange={(e) => setEmail(e.target.value)}
            value={email}
          />
          <button
            className={`bg-sky-600 hover:bg-sky-500 text-white rounded ${
              isAndroidMode
                ? 'p-4 text-lg font-semibold'
                : 'p-2'
            }`}
            onClick={handleForgotPasswordClick}
            disabled={loading}
          >
            {!!loading ? 'Sending...' : 'Send Reset Email'}
          </button>
        </>
      )}
      {!!error && <p className="text-red-500">{error}</p>}
      <button
        className={`text-sky-600 text-right hover:underline hover:text-sky-500 ${
          isAndroidMode ? 'text-base py-2' : ''
        }`}
        onClick={() => setForgotPassword(false)}
      >
        Login
      </button>
    </div>
  );
};

export default Login;
